// src/pages/Dashboard/Admin/HeuresEnseignement/hooks/useTeachingHoursDashboard.js

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { getAllEnseignants } from "../../../../../services/adherentServices";
import {
  getAllHeures_enseignement,
  createHeures_enseignement,
  updateHeures_enseignement,
  deleteHeures_enseignement,
  deleteAllHeures_enseignement,
} from "../../../../../services/heuresenseignementServices";

import {
  buildHoursIndex,
  calculateMonthlyTotalsByTeacher,
  calculateTeachingHoursStats,
  calculateTotalsByTeacher,
  filterTeachers,
  filterTeachingHours,
  getMonthSections,
  getUniqueMonths,
  sortTeachers,
  sortTeachingHours,
} from "../utils/teachingHoursUtils";

const defaultFilters = {
  query: "",
  teacherId: "",
  month: "",
};

export function useTeachingHoursDashboard() {
  const [teachers, setTeachers] = useState([]);
  const [hours, setHours] = useState([]);

  const [filters, setFilters] = useState(defaultFilters);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const [addDrawerOpen, setAddDrawerOpen] = useState(false);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [selectedHour, setSelectedHour] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const [teachersResponse, hoursResponse] = await Promise.all([
        getAllEnseignants(),
        getAllHeures_enseignement(),
      ]);

      setTeachers(sortTeachers(Array.isArray(teachersResponse) ? teachersResponse : []));
      setHours(sortTeachingHours(Array.isArray(hoursResponse) ? hoursResponse : []));
    } catch (error) {
      console.error("Erreur chargement heures enseignement :", error);
      toast.error("Erreur lors du chargement des heures d’enseignement.");
      setTeachers([]);
      setHours([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const visibleTeachers = useMemo(() => {
    return filterTeachers(teachers, filters);
  }, [teachers, filters]);

  const visibleHours = useMemo(() => {
    return filterTeachingHours(hours, visibleTeachers, filters);
  }, [hours, visibleTeachers, filters]);

  const monthOptions = useMemo(() => {
    return getUniqueMonths(hours);
  }, [hours]);

  const monthSections = useMemo(() => {
    return getMonthSections(visibleHours, filters.month);
  }, [visibleHours, filters.month]);

  const hoursIndex = useMemo(() => {
    return buildHoursIndex(visibleHours);
  }, [visibleHours]);

  const totalsByTeacher = useMemo(() => {
    return calculateTotalsByTeacher(visibleHours);
  }, [visibleHours]);

  const monthlyTotalsByTeacher = useMemo(() => {
    return calculateMonthlyTotalsByTeacher(visibleHours);
  }, [visibleHours]);

  const stats = useMemo(() => {
    return calculateTeachingHoursStats({
      teachers,
      visibleTeachers,
      hours,
      visibleHours,
      totalsByTeacher,
    });
  }, [teachers, visibleTeachers, hours, visibleHours, totalsByTeacher]);

  const openAddDrawer = () => {
    setAddDrawerOpen(true);
  };

  const closeAddDrawer = () => {
    if (saving) return;
    setAddDrawerOpen(false);
  };

  const openEditDrawer = (hour) => {
    setSelectedHour(hour);
    setEditDrawerOpen(true);
  };

  const closeEditDrawer = () => {
    if (saving) return;
    setSelectedHour(null);
    setEditDrawerOpen(false);
  };

  const handleCreateHours = async (payload) => {
    try {
      setSaving(true);

      await createHeures_enseignement(payload);

      toast.success("Heures d’enseignement ajoutées.");
      setAddDrawerOpen(false);

      await fetchData();
    } catch (error) {
      console.error("Erreur création heures enseignement :", error);
      toast.error("Erreur lors de l’ajout des heures d’enseignement.");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateHours = async (hourId, payload) => {
    if (!hourId) {
      toast.error("Impossible d’identifier la ligne à modifier.");
      return;
    }

    try {
      setSaving(true);

      await updateHeures_enseignement(hourId, payload);

      toast.success("Heures d’enseignement mises à jour.");
      setEditDrawerOpen(false);
      setSelectedHour(null);

      await fetchData();
    } catch (error) {
      console.error("Erreur mise à jour heures enseignement :", error);
      toast.error("Erreur lors de la mise à jour des heures.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteHour = async (hour) => {
    const id = typeof hour === "object" ? hour?.id : hour;

    if (!id) {
      toast.error("Impossible d’identifier la ligne à supprimer.");
      return;
    }

    if (!window.confirm("Supprimer cette entrée d’heures d’enseignement ?")) {
      return;
    }

    try {
      setDeletingId(id);

      await deleteHeures_enseignement(id);

      toast.success("Heures supprimées.");

      if (selectedHour?.id === id) {
        setSelectedHour(null);
        setEditDrawerOpen(false);
      }

      await fetchData();
    } catch (error) {
      console.error("Erreur suppression heures enseignement :", error);
      toast.error("Erreur lors de la suppression.");
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeleteAllHours = async () => {
    if (
      !window.confirm(
        "Supprimer TOUTES les heures d’enseignement ? Cette action est irréversible."
      )
    ) {
      return;
    }

    try {
      setDeletingId("__all__");

      await deleteAllHeures_enseignement();

      toast.success("Toutes les heures ont été supprimées.");

      setSelectedHour(null);
      setEditDrawerOpen(false);

      await fetchData();
    } catch (error) {
      console.error("Erreur suppression globale heures enseignement :", error);
      toast.error("Erreur lors de la suppression globale.");
    } finally {
      setDeletingId(null);
    }
  };

  const refreshData = useCallback(async () => {
    await fetchData();
  }, [fetchData]);

  return {
    teachers,
    visibleTeachers,

    hours,
    visibleHours,

    monthOptions,
    monthSections,
    hoursIndex,
    monthlyTotalsByTeacher,
    totalsByTeacher,
    stats,

    filters,
    setFilters,

    loading,
    saving,
    deletingId,

    addDrawerOpen,
    editDrawerOpen,
    selectedHour,

    openAddDrawer,
    closeAddDrawer,
    openEditDrawer,
    closeEditDrawer,

    handleCreateHours,
    handleUpdateHours,
    handleDeleteHour,
    handleDeleteAllHours,
    refreshData,
  };
}